import styled from '@emotion/styled'
import { createPropFilter, WebLink, WebLinkProps } from '@news-mono/web-common'
import { tokens } from '@news-mono/design-tokens'
import { textMixin } from '../../mixins/TextMixin/TextMixin'
import {
    breakpoint,
    breakpointSwitch,
    calcRem,
    getComponentTheme,
    metrics,
} from '../../__styling'

export type ActiveState = 'active' | 'default'

export type TagLinkProps = {
    state?: ActiveState
    forceHorizontalLayout?: boolean
}

const { neutral } = tokens.thenightly.colors.palette

export const TagLink = styled(WebLink, {
    shouldForwardProp: createPropFilter<WebLinkProps>()([
        'state',
        'forceHorizontalLayout',
    ]),
})<TagLinkProps>(({ theme, state = 'default', forceHorizontalLayout }) => {
    const tagLinkTheme = getComponentTheme(theme, 'taglinkNav').tagLink
    const defaultTagLinkTheme =
        'default' in tagLinkTheme ? tagLinkTheme.default : undefined

    return [
        [
            {
                // reset anchor styles.
                color: 'unset',
                textDecoration: 'none',

                width: '100%',
                padding: calcRem(4, 12),
                borderRadius: calcRem(40),

                ...(state === 'default' && {
                    outlineStyle: 'solid',
                    outlineWidth: calcRem(1),
                    outlineOffset: calcRem(-1),
                    outlineColor: neutral[30],
                }),

                // && workaround to emotion not setting the font-styles in the correct order.
                '&&': {
                    fontWeight: state === 'active' ? 'bold' : undefined,
                },

                ...(!forceHorizontalLayout && {
                    [breakpoint('lg')]: {
                        padding: 0,
                        borderRadius: 0,

                        outlineStyle: 'none',

                        textDecoration:
                            state === 'active' ? 'underline' : undefined,
                    },
                }),
            },
            breakpointSwitch(tagLinkTheme, ({ textType }) =>
                textMixin(theme, textType),
            ),
            breakpointSwitch(tagLinkTheme, (states) => ({
                color: states[state].textColor,
                background: states[state].background,
            })),
            forceHorizontalLayout &&
                state === 'active' && {
                    [breakpoint('lg')]: {
                        color: defaultTagLinkTheme?.active.textColor,
                        background: defaultTagLinkTheme?.active.background,
                    },
                },
        ],
    ]
})

type ResponsiveLayoutProps = {
    forceHorizontalLayout?: boolean
}

export const TagLinkWrapper = styled('nav', {
    shouldForwardProp: createPropFilter<ResponsiveLayoutProps>()([
        'forceHorizontalLayout',
    ]),
})<ResponsiveLayoutProps>(({ forceHorizontalLayout }) => ({
    display: 'flex',
    padding: calcRem(0, 24),

    [breakpoint('sm')]: {
        padding: calcRem(0, forceHorizontalLayout ? 32 : 12),
    },
    ...(forceHorizontalLayout && {
        [breakpoint('mlg')]: {
            maxWidth: calcRem(metrics.thenightly.siteMetrics.mainContentWidth),
            width: '100%',
            margin: '0 auto',
            padding: calcRem(0, 32),
        },
    }),
    [breakpoint('lg')]: {
        padding: 0,
    },
}))

export const TagLinkList = styled('ul', {
    shouldForwardProp: createPropFilter<ResponsiveLayoutProps>()([
        'forceHorizontalLayout',
    ]),
})<ResponsiveLayoutProps>(({ forceHorizontalLayout }) => ({
    // Reset ul styles.
    display: 'flex',
    margin: 0,
    padding: 0,
    listStyleType: 'none',

    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: calcRem(8),
    width: '100%',

    ...(!forceHorizontalLayout && {
        [breakpoint('lg')]: {
            flexDirection: 'column',
            gap: calcRem(12),
        },
    }),
}))

export const TagLinkListItem = styled.li({
    // Reset li styles
    display: 'flex',
    flexWrap: 'wrap',
})

export const TagLinkNavContainer = styled('div', {
    shouldForwardProp: createPropFilter<ResponsiveLayoutProps>()([
        'forceHorizontalLayout',
    ]),
})<ResponsiveLayoutProps>(({ forceHorizontalLayout }) => ({
    display: 'flex',
    flexDirection: 'column',

    ...(!forceHorizontalLayout && {
        [breakpoint('lg')]: {
            gap: calcRem(40),
        },
    }),
}))

export const Divider = styled('div', {
    shouldForwardProp: createPropFilter<ResponsiveLayoutProps>()([
        'forceHorizontalLayout',
    ]),
})<ResponsiveLayoutProps>(({ theme, forceHorizontalLayout }) => {
    const tagLinkTheme = getComponentTheme(theme, 'taglinkNav').divider
    return {
        display: 'none',

        ...(!forceHorizontalLayout && {
            [breakpoint('lg')]: {
                display: 'flex',
                height: calcRem(1),
                background: tagLinkTheme.color,
            },
        }),
    }
})

export const TrailingContent = styled('div', {
    shouldForwardProp: createPropFilter<ResponsiveLayoutProps>()([
        'forceHorizontalLayout',
    ]),
})<ResponsiveLayoutProps>(({ forceHorizontalLayout }) => ({
    display: 'none',

    ...(!forceHorizontalLayout && {
        [breakpoint('lg')]: {
            display: 'flex',
            flexDirection: 'column',
            gap: calcRem(24),
        },
    }),
}))
